/**
 * Catégories KYC alignées sur le parcours freelance « société » (SAS, SARL…).
 * Mêmes slugs que `freelancer_legal_documents` : registre-entreprises, attestation-vigilance.
 */
export const PORTAGE_KYC_CATEGORIES_FR = ['registre-entreprises', 'attestation-vigilance'] as const

/** Étranger : document d'immatriculation uniquement (comme freelance société hors France). */
export const PORTAGE_KYC_CATEGORIES_FOREIGN = ['registre-entreprises'] as const

export const PORTAGE_KYC_RC_INSURANCE = 'rc-insurance' as const

export type PortageKycCategory =
  | (typeof PORTAGE_KYC_CATEGORIES_FR)[number]
  | (typeof PORTAGE_KYC_CATEGORIES_FOREIGN)[number]
  | typeof PORTAGE_KYC_RC_INSURANCE

export interface PortageKycOptions {
  requireRcInsurance?: boolean
}

/** Anciennes catégories portage (LOT 2 initial) → slugs freelance. */
const LEGACY_PORTAGE_KYC_CATEGORY_ALIASES: Record<string, PortageKycCategory> = {
  kbis: 'registre-entreprises',
  vigilance: 'attestation-vigilance',
  registration: 'registre-entreprises',
}

export interface PortageLegalDocumentRow {
  category: string
  file_name: string
  file_key?: string | null
  valid_until?: string | null
  validation_status?: string | null
  updated_at?: string | null
}

export function normalizePortageKycCategory(category: string): string {
  return LEGACY_PORTAGE_KYC_CATEGORY_ALIASES[category] ?? category
}

/** Variantes DB possibles pour une catégorie normalisée (legacy + slug canonique). */
export function getPortageKycCategoryDbVariants(category: string): string[] {
  const normalized = normalizePortageKycCategory(category)
  const variants = new Set<string>([normalized])
  for (const [legacy, canonical] of Object.entries(LEGACY_PORTAGE_KYC_CATEGORY_ALIASES)) {
    if (canonical === normalized) {
      variants.add(legacy)
    }
  }
  return [...variants]
}

/**
 * Une seule entrée par catégorie normalisée (comme getDocumentByCategory côté freelance).
 * En cas de doublons legacy, conserve le document le plus récent.
 */
export function dedupePortageLegalDocuments<T extends PortageLegalDocumentRow>(
  documents: T[]
): T[] {
  const byCategory = new Map<string, T>()

  for (const doc of documents) {
    const key = normalizePortageKycCategory(doc.category)
    const normalizedDoc = { ...doc, category: key }
    const existing = byCategory.get(key)
    if (!existing) {
      byCategory.set(key, normalizedDoc)
      continue
    }

    const docUpdated = doc.updated_at ?? ''
    const existingUpdated = existing.updated_at ?? ''
    if (docUpdated >= existingUpdated) {
      byCategory.set(key, normalizedDoc)
    }
  }

  return [...byCategory.values()]
}

export function getPortageDocumentByCategory<T extends PortageLegalDocumentRow>(
  documents: T[],
  category: string
): T | undefined {
  const normalized = normalizePortageKycCategory(category)
  return dedupePortageLegalDocuments(documents).find(
    (doc) => normalizePortageKycCategory(doc.category) === normalized
  )
}

/** Valide qu'une catégorie est autorisée pour le questionnaire (pays + RC Pro). */
export function resolveAllowedPortageKycCategory(
  category: string,
  country: string | null | undefined,
  options?: PortageKycOptions
): PortageKycCategory | null {
  const normalized = normalizePortageKycCategory(category)
  const allowed = getRequiredPortageKycCategories(country, options)
  return allowed.includes(normalized as PortageKycCategory)
    ? (normalized as PortageKycCategory)
    : null
}

export function getRequiredPortageKycCategories(
  country: string | null | undefined,
  options?: PortageKycOptions
): PortageKycCategory[] {
  const normalized = (country || 'FR').toUpperCase()
  const base: PortageKycCategory[] =
    normalized === 'FR' ? [...PORTAGE_KYC_CATEGORIES_FR] : [...PORTAGE_KYC_CATEGORIES_FOREIGN]

  if (options?.requireRcInsurance) {
    return [...base, PORTAGE_KYC_RC_INSURANCE]
  }
  return base
}

function isDocumentExpired(validUntil: string | null | undefined): boolean {
  if (!validUntil) return false
  const today = new Date().toISOString().split('T')[0]
  return validUntil < today
}

/** True si tous les documents requis sont présents et non expirés. */
export function isPortageCompanyKycComplete(
  country: string | null | undefined,
  documents: PortageLegalDocumentRow[],
  options?: PortageKycOptions
): boolean {
  const required = getRequiredPortageKycCategories(country, options)
  const byCategory = new Map(
    dedupePortageLegalDocuments(documents).map((doc) => [
      normalizePortageKycCategory(doc.category),
      doc,
    ])
  )
  return required.every((category) => {
    const doc = byCategory.get(category)
    if (!doc?.file_key?.trim() && !doc?.file_name?.trim()) return false
    // 'approved' seul est complet (couvre 'rejected'/'pending_review' → false).
    if (doc.validation_status !== 'approved') return false
    return !isDocumentExpired(doc.valid_until)
  })
}

/**
 * True si tous les documents requis ont été téléversés (validation OCR ignorée).
 * Permet de poursuivre le questionnaire même en cas de faux rejet OCR.
 */
export function isPortageCompanyKycDocumentsUploaded(
  country: string | null | undefined,
  documents: PortageLegalDocumentRow[],
  options?: PortageKycOptions
): boolean {
  const required = getRequiredPortageKycCategories(country, options)
  const byCategory = new Map(
    dedupePortageLegalDocuments(documents).map((doc) => [
      normalizePortageKycCategory(doc.category),
      doc,
    ])
  )
  return required.every((category) => {
    const doc = byCategory.get(category)
    return Boolean(doc?.file_key?.trim() || doc?.file_name?.trim())
  })
}

export function isPortageCompanyInfoComplete(company: {
  name?: string | null
  siren_number?: string | null
  address?: string | null
  country?: string | null
  company_type?: string | null
  registre_location?: string | null
  company_title?: string | null
  capital?: string | number | null
}): boolean {
  const baseComplete = Boolean(
    company.name?.trim() &&
      company.siren_number?.trim() &&
      company.address?.trim() &&
      company.country?.trim()
  )
  if (!baseComplete) return false

  const normalizedCountry = (company.country || 'FR').toUpperCase()
  if (normalizedCountry !== 'FR') {
    return true
  }

  return Boolean(
    company.company_type?.trim() &&
      company.registre_location?.trim() &&
      company.capital != null &&
      String(company.capital).trim()
  )
}

/** Étape initiale du wizard : référent seule si RIB + KYC déjà validés. */
export function getPortageQuestionnaireInitialStep(input: {
  country?: string | null
  companyStatus?: string | null
  /** Tous les documents requis téléversés (OCR ignoré). */
  kycDocumentsUploaded: boolean
  /** KYC société entièrement validé (docs approuvés, non expirés). */
  kycValidated: boolean
  infoComplete: boolean
  bankAccountComplete: boolean
}): 'nationality' | 'company_info' | 'bank_account' | 'kyc' | 'referent' {
  if (input.companyStatus === 'active' && input.kycValidated && input.bankAccountComplete) {
    return 'referent'
  }
  if (!input.country) {
    return 'nationality'
  }
  if (!input.infoComplete) {
    return 'company_info'
  }
  if (!input.kycDocumentsUploaded) {
    return 'kyc'
  }
  if (!input.bankAccountComplete) {
    return 'bank_account'
  }
  return 'referent'
}
